/**
 * ResponsiveMediaPlayer Component
 *
 * Main orchestrator component that provides adaptive player UI based on device type.
 *
 * Desktop: Classic bottom bar player with full controls
 * Mobile:
 *   - Minimized state: Bottom bar with basic controls + full-screen chat
 *   - Fullscreen state: Full-screen Audible-style player + drawer chat
 *
 * Manages:
 * - Device detection and responsive layout switching
 * - Player state (minimized/fullscreen on mobile)
 * - Chat overlay coordination with player state
 * - Single MediaPlayer instance shared across all views
 */

import { MediaPlayer, MediaProvider, Track } from "@vidstack/react";
import type { MediaPlayerInstance } from "@vidstack/react";
import { useRef, useState, useEffect } from "react";
import { usePlayerStore, getPlayerActions } from "@/lib/player/player.store";
import { usePlayerSync } from "./hooks/usePlayerSync.hook";
import { usePlaybackProgressSync } from "./hooks/usePlaybackProgressSync.hook";

// Desktop player content
import { DesktopPlayerContent } from "./desktopPlayerContent.component";

// Mobile components
import { MobilePlayerFullscreen } from "./mobilePlayerFullscreen.component";
import { MobilePlayerMinimized } from "./mobilePlayerMinimized.component";
import { MobileChaptersOverlay } from "./mobileChaptersOverlay.component";
import { TranscriptPanel } from "./transcriptPanel.component";
import { GlobalChat } from "@/features/chat/components/globalChat.component";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

// Mobile player state type
type MobilePlayerState = "minimized" | "fullscreen";

interface ResponsiveMediaPlayerProps {
	/** Whether to offset the desktop player for the sidebar (used in MainLayout) */
	offsetForSidebar?: boolean;
	/** Preview limit in seconds for non-authenticated users */
	previewLimit?: number | null;
	/** Callback when user tries to seek beyond preview limit */
	onPreviewLimitReached?: () => void;
}

export function ResponsiveMediaPlayer({
	offsetForSidebar = false,
	previewLimit = null,
	onPreviewLimitReached,
}: ResponsiveMediaPlayerProps = {}) {
	const playerRef = useRef<MediaPlayerInstance | null>(null);
	const [isMobile, setIsMobile] = useState(false);
	const [mobilePlayerState, setMobilePlayerState] =
		useState<MobilePlayerState>("minimized");
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isTranscriptOpen, setIsTranscriptOpen] = useState(false); // desktop only

	// Sync Vidstack state with Zustand store
	const { vidstackWaiting } = usePlayerSync(playerRef);

	// Sync playback progress with backend
	usePlaybackProgressSync();

	// Get current media info for chat context
	const podWorkspaceId = usePlayerStore((state) => state.podWorkspaceId);
	const chapters = usePlayerStore((state) => state.chapters);
	const currentMedia = usePlayerStore((state) => state.currentMedia);
	const podcastId = usePlayerStore((state) => state.podcastId);
	const showChapters = usePlayerStore((state) => state.showChapters);

	// Detect mobile device
	useEffect(() => {
		const checkMobile = () => {
			// Check both screen width and touch capability
			const isMobileWidth = window.innerWidth < 768;
			const isTouchDevice =
				"ontouchstart" in window || navigator.maxTouchPoints > 0;
			setIsMobile(isMobileWidth && isTouchDevice);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	/**
	 * Handle volume change events from Vidstack
	 */
	const handleVolumeChange = () => {
		if (playerRef.current) {
			getPlayerActions().updateMuteState(playerRef.current.muted);
		}
	};

	/**
	 * Handle playback rate change events from Vidstack
	 */
	const handleRateChange = () => {
		if (playerRef.current) {
			getPlayerActions().updatePlaybackSpeed(playerRef.current.playbackRate);
		}
	};

	/**
	 * Handle media ready event (canPlay)
	 */
	const handleCanPlay = () => {
		const player = playerRef.current;
		if (!player) return;

		const state = usePlayerStore.getState();
		const { podcastId, pendingRestore, autoPlay: shouldAutoPlay } = state;

		if (
			pendingRestore &&
			pendingRestore.podcastId === podcastId &&
			player.duration > 0 &&
			Number.isFinite(player.duration)
		) {
			const clampedPosition = Math.max(
				0,
				Math.min(pendingRestore.position, player.duration),
			);

			player.currentTime = clampedPosition;
			usePlayerStore.setState({ pendingRestore: null });
		}

		if (shouldAutoPlay) {
			// Ensure the global player store reflects that playback has started
			// as soon as we auto-play, so desktop and mobile UIs update instantly.
			player
				.play()
				.then(() => {
					usePlayerStore.setState({
						autoPlay: false,
						isPaused: false,
						isPlaying: true,
					});
				})
				.catch((error) => {
					console.error("Failed to auto-play media:", error);
					usePlayerStore.setState({
						autoPlay: false,
						isPaused: true,
						isPlaying: false,
					});
				});
		}
	};

	/**
	 * Handle media errors
	 */
	const handleError = () => {
		console.error("Media error occurred");
	};

	/**
	 * Handle provider setup
	 * This is called when the media provider is set up and ready
	 */
	const handleProviderSetup = () => {
		if (playerRef.current) {
			console.log(
				"MediaPlayer provider setup, setting ref in store:",
				playerRef.current,
			);
			getPlayerActions().setPlayerRef(playerRef.current);
		}
	};

	// Mobile player state handlers
	const handleMinimize = () => {
		setMobilePlayerState("minimized");
		setIsChatOpen(false);
	};

	const handleExpand = () => {
		setMobilePlayerState("fullscreen");
		setIsChatOpen(false);
	};

	const handleOpenChat = () => {
		setIsChatOpen(true);
	};

	const handleOpenChapters = () => {
		getPlayerActions().toggleChaptersList();
	};

	const handleOpenTranscript = () => {
		setIsTranscriptOpen(true);
		AnalyticsService.track("transcript_opened", {
			platform: "desktop",
			podcast_id: podcastId,
		});
	};

	return (
		<MediaPlayer
			ref={playerRef}
			// Use generic container style that doesn't interfere with fixed children
			// But ensures Vidstack is rendered
			className="group"
			title={currentMedia?.title}
			src={currentMedia?.src}
			preload="metadata"
			onProviderSetup={handleProviderSetup}
			onError={handleError}
			onVolumeChange={handleVolumeChange}
			onRateChange={handleRateChange}
			onCanPlay={handleCanPlay}
		>
			<MediaProvider>
				<Track
					kind="chapters"
					label="Chapters"
					type="json"
					content={{ cues: chapters }}
					default
				/>
			</MediaProvider>

			{/* Only render player UI when media is loaded */}
			{currentMedia && (
				<>
					{/* Render appropriate UI based on device */}
					{isMobile ? (
						<>
							{/* Mobile UI */}
							{mobilePlayerState === "fullscreen" ? (
								<MobilePlayerFullscreen
									playerRef={playerRef}
									onMinimize={handleMinimize}
									onOpenChat={handleOpenChat}
									onOpenChapters={handleOpenChapters}
								/>
							) : (
								<MobilePlayerMinimized onExpand={handleExpand} />
							)}

							{/* Mobile Chapters Overlay */}
							<MobileChaptersOverlay
								isOpen={showChapters}
								onClose={() => getPlayerActions().setShowChapters(false)}
							/>
						</>
					) : (
						/* Desktop UI */
						<DesktopPlayerContent
							playerRef={playerRef}
							vidstackWaiting={vidstackWaiting}
							offsetForSidebar={offsetForSidebar}
							previewLimit={previewLimit}
							onPreviewLimitReached={onPreviewLimitReached}
							onOpenTranscript={handleOpenTranscript}
						/>
					)}

					{/* Transcript slide-in panel – desktop only (mobile handles inline) */}
					{!isMobile && (
						<TranscriptPanel
							open={isTranscriptOpen}
							onOpenChange={setIsTranscriptOpen}
						/>
					)}

					{/* Global Chat - works for both mobile and desktop */}
					{podWorkspaceId && (
						<GlobalChat
							defaultOpen={isChatOpen}
							onOpenChange={setIsChatOpen}
							podWorkspaceId={podWorkspaceId}
							workspaceTitle={currentMedia?.subtitle}
							hideTrigger={true}
						/>
					)}
				</>
			)}
		</MediaPlayer>
	);
}
